import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select, Input } from '../../components/ui/Input';
import { Field } from '../../components/ui/Field';
import { Badge } from '../../components/ui/Badge';
import { Callout } from '../../components/ui/Callout';
import { Icon } from '../../components/ui/Icon';
import { useWallet } from '../../context/WalletContext';
import { useWallets } from '../../hooks/useWallets';
import orderImportService, {
  ImportCandidate,
  ImportParseResult,
  BatchOrderInput,
  BatchSubmitResult,
} from '../../services/orderImportService';
import { ShippingRate } from '../../services/shippingService';
import { useBatchRates, pickRate } from './useBatchRates';
import RowDetailDialog from './RowDetailDialog';

interface ImportOrdersWizardProps {
  onClose: (didCreate: boolean) => void;
}

type RowMode = 'submit' | 'draft';
interface RowSelection {
  rateId?: string;
  mode: RowMode;
}

const readDefaultShipping = (): { id: string; name: string } => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return { id: user?.default_shipping_id || '', name: user?.default_shipping_name || '' };
  } catch {
    return { id: '', name: '' };
  }
};

const ImportOrdersWizard: React.FC<ImportOrdersWizardProps> = ({ onClose }) => {
  const { paymentMethods } = useWallet();
  const { wallets } = useWallets();
  // Bulk orders are paid from the wallet, so rates are fetched in the wallet currency (not the display toggle).
  // Use the richest wallet: a leftover small balance in another currency (e.g. USD) must not win over the main ILS wallet.
  const walletCurrency = [...(wallets as Array<{ currency_code: string; balance: string | number }>)].sort((a, b) => Number(b.balance) - Number(a.balance))[0]?.currency_code || 'ILS';
  const cards = useMemo(() => paymentMethods.filter((p) => p.type === 'card'), [paymentMethods]);

  const [step, setStep] = useState<'upload' | 'review'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportParseResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { rowRates, isFetching, fetchAll, reset } = useBatchRates();
  const [selection, setSelection] = useState<Record<number, RowSelection>>({});
  const [detailRow, setDetailRow] = useState<ImportCandidate | null>(null);

  const initial = readDefaultShipping();
  const [senderName, setSenderName] = useState(initial.name);
  const [senderId, setSenderId] = useState(initial.id);
  const [shippingDescription, setShippingDescription] = useState('');
  const [payMethod, setPayMethod] = useState<'wallet' | 'card'>('wallet');
  const [payMethodId, setPayMethodId] = useState<string>('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<BatchSubmitResult | null>(null);

  const candidates = result?.orders ?? [];

  // ---- Upload ----
  const handleDownloadTemplate = async () => {
    try {
      await orderImportService.downloadTemplate();
    } catch {
      setParseError('Failed to download template.');
    }
  };

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    setParseError(null);
    try {
      const res = await orderImportService.parseImport(file);
      setResult(res);
      // Initialise selection: rows with errors can only be drafts.
      const sel: Record<number, RowSelection> = {};
      res.orders.forEach((o) => {
        sel[o.rowNumber] = { mode: o.errors.length ? 'draft' : 'submit' };
      });
      setSelection(sel);
      setStep('review');
      fetchAll(res.orders, walletCurrency);
    } catch (err: any) {
      setParseError(err?.response?.data?.message || err?.message || 'Failed to parse spreadsheet.');
    } finally {
      setParsing(false);
    }
  };

  // Default each row to its cheapest rate once rates arrive.
  useEffect(() => {
    setSelection((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(rowRates).forEach(([rowNumber, state]) => {
        const rn = Number(rowNumber);
        if (state.status === 'done' && state.rates.length && !next[rn]?.rateId) {
          const cheapest = pickRate(state.rates, 'cheapest');
          if (cheapest) {
            next[rn] = { ...(next[rn] || { mode: 'submit' }), rateId: cheapest.id };
            changed = true;
          }
        }
      });
      return changed ? next : prev;
    });
  }, [rowRates]);

  const applyToAll = (strategy: 'cheapest' | 'fastest') => {
    setSelection((prev) => {
      const next = { ...prev };
      candidates.forEach((c) => {
        const state = rowRates[c.rowNumber];
        if (state?.status === 'done' && state.rates.length) {
          const chosen = pickRate(state.rates, strategy);
          if (chosen) next[c.rowNumber] = { ...(next[c.rowNumber] || { mode: 'submit' }), rateId: chosen.id };
        }
      });
      return next;
    });
  };

  const setRowMode = (rowNumber: number, mode: RowMode) =>
    setSelection((prev) => ({ ...prev, [rowNumber]: { ...(prev[rowNumber] || {}), mode } }));

  const setRowRate = (rowNumber: number, rateId: string) =>
    setSelection((prev) => ({ ...prev, [rowNumber]: { ...(prev[rowNumber] || { mode: 'submit' }), rateId } }));

  const rateFor = (c: ImportCandidate): ShippingRate | undefined =>
    rowRates[c.rowNumber]?.rates.find((r) => r.id === selection[c.rowNumber]?.rateId);

  // ---- Totals ----
  const submitRows = candidates.filter((c) => selection[c.rowNumber]?.mode === 'submit' && c.errors.length === 0);
  const draftRows = candidates.filter(
    (c) => selection[c.rowNumber]?.mode === 'draft' || c.errors.length > 0
  );
  const submitWithRate = submitRows.filter((c) => rateFor(c));
  const batchTotal = submitWithRate.reduce((sum, c) => {
    const r = rateFor(c)!;
    return sum + r.price + (c.customs.insuranceAmount || 0);
  }, 0);
  const batchCurrency = submitWithRate[0] ? rateFor(submitWithRate[0])!.currency : walletCurrency;
  // Wallets are per-currency; the batch is charged from the wallet matching the rate currency.
  const walletBalance = Number(
    (wallets as Array<{ currency_code: string; balance: string | number }>).find((w) => w.currency_code === batchCurrency)?.balance ?? 0
  );
  const walletShort = payMethod === 'wallet' && submitWithRate.length > 0 && walletBalance < batchTotal;

  const submitMissingRate = submitRows.length - submitWithRate.length;
  const canSubmit =
    !submitting &&
    !walletShort &&
    candidates.length > 0 &&
    (submitWithRate.length > 0 || draftRows.length > 0) &&
    (submitWithRate.length === 0 ||
      (!!senderName && !!senderId && (payMethod === 'wallet' || (payMethod === 'card' && !!payMethodId))));

  const buildOrders = (): BatchOrderInput[] => {
    const out: BatchOrderInput[] = [];
    candidates.forEach((c) => {
      const sel = selection[c.rowNumber];
      const rate = rateFor(c);
      const isDraft = sel?.mode !== 'submit' || c.errors.length > 0 || !rate;
      const round2 = (n: number) => Math.round(n * 100) / 100;
      const insurance = round2(c.customs.insuranceAmount || 0);
      const shipping = round2(rate?.price ?? 0);
      // Per-row "Shipping Description" from the sheet wins; the wizard field is a batch fallback.
      const description = (c.customs.documentDescription || '').trim() || shippingDescription.trim();
      out.push({
        save_as_draft: isDraft,
        addresses: c.addresses,
        parcels: c.parcels,
        customs: description ? { ...c.customs, documentDescription: description } : c.customs,
        isDocument: c.isDocument,
        currency: rate?.currency || c.currency,
        referenceNumber: c.reference,
        selectedRate: rate
          ? {
              id: rate.id,
              name: rate.name,
              provider: rate.provider,
              price: shipping,
              base_price: round2(rate.base_price),
              has_commission: rate.has_commission,
              currency: rate.currency,
              deliveryTime: rate.transit_time,
            }
          : null,
        price: rate ? { shipping, insurance, tax: 0, total: round2(shipping + insurance) } : null,
        carrier: rate?.provider ?? '',
        service: rate?.name ?? '',
        deliveryEstimate: rate?.estimated_delivery_date ?? null,
      });
    });
    return out;
  };

  // Export the uploaded sheet with the fetched rate + selection per row (works before any order exists).
  const handleExport = async () => {
    if (!file) return;
    setExporting(true);
    setExportError(null);
    try {
      await orderImportService.exportWithRates(
        file,
        candidates.map((c) => {
          const rate = rateFor(c);
          const rateError = rowRates[c.rowNumber]?.error;
          return {
            rowNumber: c.rowNumber,
            carrier: rate?.carrier_name || rate?.provider_display_name || rate?.provider || '',
            service: rate?.name || '',
            price: rate ? Number(rate.price) : null,
            currency: rate?.currency || '',
            transit: rate ? rate.transit_time || (rate.transit_days ? `${rate.transit_days} days` : '') : '',
            mode: c.errors.length ? 'draft' : selection[c.rowNumber]?.mode || 'submit',
            status: c.errors.length ? 'error' : c.warnings.length ? 'warning' : 'ok',
            notes: [...c.errors, ...c.warnings, rateError].filter(Boolean).join('; '),
          };
        })
      );
    } catch (err: any) {
      setExportError(err?.response?.data?.message || err?.message || 'Failed to export sheet.');
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await orderImportService.submitBatch({
        paymentMethod: payMethod,
        paymentMethodId: payMethod === 'card' ? payMethodId : null,
        default_shipping_id: senderId,
        default_shipping_name: senderName,
        orders: buildOrders(),
      });
      setSubmitResult(res);
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || err?.message || 'Failed to create batch.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    const created = !!submitResult;
    reset();
    onClose(created);
  }, [submitResult, reset, onClose]);

  // ---- Render ----
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg, #fff)',
        zIndex: 90,
        overflow: 'auto',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 1160, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Import orders from sheet</h2>
            <div className="muted" style={{ fontSize: 13 }}>
              {step === 'upload' ? 'Upload your filled template to begin.' : `${candidates.length} order(s) parsed.`}
            </div>
          </div>
          <Button variant="ghost" onClick={handleClose}>
            <Icon name="x" size={14} />
            <span>Close</span>
          </Button>
        </div>

        {step === 'upload' && (
          <Card style={{ padding: 24 }}>
            <Callout tone="info" style={{ marginBottom: 16 }}>
              <span>
                Each row becomes one order (one parcel, up to 7 items). Rates and payment are chosen on the next step.
              </span>
            </Callout>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
              <Button variant="secondary" onClick={handleDownloadTemplate}>
                <Icon name="file" size={14} />
                <span>Download template</span>
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
                <Icon name="package" size={14} />
                <span>{file ? file.name : 'Choose file'}</span>
              </Button>
              <Button variant="blue" disabled={!file || parsing} onClick={handleParse}>
                {parsing ? 'Parsing…' : 'Parse file'}
              </Button>
            </div>

            {parseError && (
              <Callout tone="warn">
                <Icon name="alertCircle" size={16} />
                <span>{parseError}</span>
              </Callout>
            )}
          </Card>
        )}

        {step === 'review' && submitResult && (
          <Card style={{ padding: 24 }}>
            <Callout tone="ok" style={{ marginBottom: 14 }}>
              <span>
                Batch processed — {submitResult.submitted.length} submitted, {submitResult.drafts.length} draft(s)
                {submitResult.payment ? ` · charged ${submitResult.payment.total} ${submitResult.payment.currency}` : ''}.
              </span>
            </Callout>
            {submitResult.failures.length > 0 && (
              <Callout tone="warn" style={{ marginBottom: 14 }}>
                <span>{submitResult.failures.length} row(s) failed: {submitResult.failures.map((f) => f.error).join('; ')}</span>
              </Callout>
            )}
            <Button variant="blue" onClick={handleClose}>Done</Button>
          </Card>
        )}

        {step === 'review' && !submitResult && (
          <>
            <Card style={{ padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <Badge tone="info">{result?.summary.totalRows} rows</Badge>
                {(result?.summary.rowsWithErrors ?? 0) > 0 && (
                  <Badge tone="err">{result?.summary.rowsWithErrors} with errors</Badge>
                )}
                {(result?.summary.rowsWithWarnings ?? 0) > 0 && (
                  <Badge tone="warn">{result?.summary.rowsWithWarnings} with warnings</Badge>
                )}
                <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                  {isFetching && <span className="muted" style={{ fontSize: 12 }}>Fetching rates…</span>}
                  <Button size="sm" variant="ghost" disabled={isFetching || exporting || !file} onClick={handleExport}>
                    <Icon name="download" size={13} />
                    <span>{exporting ? 'Exporting…' : 'Export sheet'}</span>
                  </Button>
                  <span className="muted" style={{ fontSize: 12 }}>Apply to all:</span>
                  <Button size="sm" disabled={isFetching} onClick={() => applyToAll('cheapest')}>Cheapest</Button>
                  <Button size="sm" disabled={isFetching} onClick={() => applyToAll('fastest')}>Fastest</Button>
                  <Button size="sm" disabled={isFetching} onClick={() => candidates.length && fetchAll(candidates, walletCurrency)}>
                    <Icon name="refresh" size={13} />
                    <span>Refetch</span>
                  </Button>
                </div>
              </div>
            </Card>

            {exportError && (
              <Callout tone="warn" style={{ marginBottom: 16 }}>
                <Icon name="alertCircle" size={16} />
                <span>{exportError}</span>
              </Callout>
            )}

            <Card style={{ marginBottom: 16 }}>
              <table className="table" style={{ fontSize: 13 }}>
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Route</th>
                    <th>Weight</th>
                    <th>Rate</th>
                    <th>Mode</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => {
                    const rs = rowRates[c.rowNumber];
                    const sel = selection[c.rowNumber];
                    const hasError = c.errors.length > 0;
                    const parcel = c.parcels[0];
                    return (
                      <tr key={c.rowNumber}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{c.reference}</div>
                          <div className="muted" style={{ fontSize: 11 }}>#{c.rowNumber}</div>
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {c.addresses.from?.countryCode} → {c.addresses.to?.countryCode}
                          <div className="muted" style={{ fontSize: 11 }}>
                            {c.addresses.to?.city?.name || c.addresses.to?.city || ''}
                          </div>
                        </td>
                        <td style={{ fontSize: 12 }}>{parcel?.weight ?? 0} kg</td>
                        <td>
                          {hasError ? (
                            <span className="muted" style={{ fontSize: 12 }}>—</span>
                          ) : rs?.status === 'loading' ? (
                            <span className="muted" style={{ fontSize: 12 }}>Loading…</span>
                          ) : rs?.status === 'error' ? (
                            <span style={{ fontSize: 12, color: 'var(--err, #c00)' }}>{rs.error || 'No rates'}</span>
                          ) : rs?.rates.length ? (
                            <Select
                              value={sel?.rateId || ''}
                              onChange={(e) => setRowRate(c.rowNumber, e.target.value)}
                              style={{ minWidth: 200 }}
                            >
                              {rs.rates.map((r) => (
                                <option key={r.id} value={r.id}>
                                  {r.carrier_name || r.provider} · {r.name} — {Number(r.price).toFixed(2)} {r.currency} ({r.transit_time || `${r.transit_days}d`})
                                </option>
                              ))}
                            </Select>
                          ) : (
                            <span className="muted" style={{ fontSize: 12 }}>—</span>
                          )}
                        </td>
                        <td>
                          <Select
                            value={hasError ? 'draft' : sel?.mode || 'submit'}
                            disabled={hasError}
                            onChange={(e) => setRowMode(c.rowNumber, e.target.value as RowMode)}
                            style={{ width: 110 }}
                          >
                            <option value="submit">Submit</option>
                            <option value="draft">Draft</option>
                          </Select>
                        </td>
                        <td>
                          {hasError ? (
                            <Badge tone="err" dot>Error</Badge>
                          ) : c.warnings.length ? (
                            <Badge tone="warn" dot>Warning</Badge>
                          ) : (
                            <Badge tone="ok" dot>OK</Badge>
                          )}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <Button size="xs" variant="ghost" onClick={() => setDetailRow(c)}>View</Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>

            <Card style={{ padding: 16 }}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <Field label="Sender name (for label)">
                  <Input value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Sender name" />
                </Field>
                <Field label="Sender ID / Tax number">
                  <Input value={senderId} onChange={(e) => setSenderId(e.target.value)} placeholder="Sender ID" />
                </Field>
                <Field label="Short description of the shipping details">
                  <Input
                    value={shippingDescription}
                    onChange={(e) => setShippingDescription(e.target.value)}
                    placeholder="e.g. Personal gift items, no commercial value"
                    style={{ minWidth: 260 }}
                  />
                </Field>
                <Field label="Payment method">
                  <Select value={payMethod} onChange={(e) => setPayMethod(e.target.value as 'wallet' | 'card')} style={{ width: 160 }}>
                    <option value="wallet">Wallet</option>
                    <option value="card">Card</option>
                  </Select>
                </Field>
                {payMethod === 'card' && (
                  <Field label="Card">
                    <Select value={payMethodId} onChange={(e) => setPayMethodId(e.target.value)} style={{ minWidth: 200 }}>
                      <option value="">Select card…</option>
                      {cards.map((c) => (
                        <option key={c.id} value={c.id}>{c.name} •••• {c.last4}</option>
                      ))}
                    </Select>
                  </Field>
                )}
                <div style={{ marginInlineStart: 'auto', textAlign: 'right' }}>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {submitWithRate.length} to submit · {draftRows.length} draft(s)
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>
                    {batchTotal.toFixed(2)} {batchCurrency}
                  </div>
                  {payMethod === 'wallet' && (
                    <div className="muted" style={{ fontSize: 12 }}>
                      {batchCurrency} wallet balance: {walletBalance.toFixed(2)}
                    </div>
                  )}
                </div>
              </div>

              {walletShort && (
                <Callout tone="warn" style={{ marginTop: 12 }}>
                  <Icon name="alertCircle" size={16} />
                  <span>
                    Your {batchCurrency} wallet has {walletBalance.toFixed(2)} but the batch total is {batchTotal.toFixed(2)} {batchCurrency}. Top up that wallet or pay by card.
                  </span>
                </Callout>
              )}
              {submitMissingRate > 0 && (
                <Callout tone="warn" style={{ marginTop: 12 }}>
                  <span>{submitMissingRate} row(s) marked Submit have no rate selected and will be saved as drafts.</span>
                </Callout>
              )}
              {submitError && (
                <Callout tone="warn" style={{ marginTop: 12 }}>
                  <Icon name="alertCircle" size={16} />
                  <span>{submitError}</span>
                </Callout>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
                <Button variant="ghost" onClick={handleClose}>Cancel</Button>
                <Button variant="blue" disabled={!canSubmit} onClick={handleSubmit}>
                  {submitting
                    ? 'Processing…'
                    : submitWithRate.length > 0
                      ? `Pay & create ${submitWithRate.length + draftRows.length} order(s)`
                      : `Create ${draftRows.length} draft(s)`}
                </Button>
              </div>
            </Card>
          </>
        )}

        <RowDetailDialog candidate={detailRow} open={!!detailRow} onOpenChange={(o) => !o && setDetailRow(null)} />
      </div>
    </div>
  );
};

export default ImportOrdersWizard;
